// src/components/InfoDialog.tsx
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription
} from "@/components/ui/dialog";
import { Info, FileText, Server, Database, Layers, Code } from "lucide-react";

interface InfoDialogProps {
  trigger?: React.ReactNode;
}

const InfoDialog: React.FC<InfoDialogProps> = ({ trigger }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <button className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-gray-700/50">
            <Info className="h-5 w-5" />
            <span className="sr-only">Information</span>
          </button>
        )}
      </DialogTrigger>
      
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto custom-scrollbar bg-gray-900/95 border-gray-700 rounded-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Info className="h-5 w-5 text-orange-300" />
            <span>Om Process Viewer</span>
          </DialogTitle>
          
          <DialogDescription className="sr-only">
            Information om Process Viewer, dess funktioner och datakällor
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4 text-gray-300 space-y-6">
          <p>
            Process Viewer är en applikation för att visualisera och navigera processer från 
            SharePoint. Den ger en strukturerad och enkel översikt över processerna 
            med tillhörande dokument, deltagare och ansvarsområden.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
              <h3 className="flex items-center gap-2 text-white font-medium mb-2">
                <Layers className="h-4 w-4 text-orange-300" />
                Processnavigering
              </h3>
              <p className="text-sm">
                Processnavigeringen överst låter dig välja huvudprocess och delprocess.
                Efter val av dessa visas tillgängliga moment i vänsterkolumnen. 
                Välj ett moment för att se dess detaljer, dokument och deltagare.
              </p>
            </div>
            
            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
              <h3 className="flex items-center gap-2 text-white font-medium mb-2">
                <Server className="h-4 w-4 text-orange-300" />
                SharePoint-integration
              </h3>
              <p className="text-sm">
                Applikationen hämtar all data från SharePoint-listan "Process Viwer CMS".
                Dokumentlänkar i applikationen pekar direkt till dokument i SharePoint. 
                Autentisering sker via Azure AD.
              </p>
            </div>
            
            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
              <h3 className="flex items-center gap-2 text-white font-medium mb-2">
                <Database className="h-4 w-4 text-orange-300" />
                SharePoint-struktur
              </h3>
              <p className="text-sm">
                SharePoint-listan innehåller följande fält:
              </p>
              <ul className="text-xs text-gray-400 mt-2 space-y-1">
                <li>• <strong>Title</strong>: Huvudprocess</li>
                <li>• <strong>field_0</strong>: Delprocess</li>
                <li>• <strong>field_2</strong>: Moment</li>
                <li>• <strong>field_3</strong>: Beskrivning</li>
                <li>• <strong>field_4</strong>: Dokument (format: "Titel (URL)")</li>
                <li>• <strong>Listordning</strong>: Ordningen i processen</li>
                <li>• <strong>Viktigt</strong>: Viktig information att framhäva</li>
                <li>• <strong>Detaljeradbeskrivning</strong>: Utförlig beskrivning</li>
                <li>• <strong>Deltagareochansvar1</strong>: Deltagare och ansvar (alternativ 1)</li>
                <li>• <strong>Deltagareochansvar2</strong>: Deltagare och ansvar (alternativ 2)</li>
                <li>• <strong>Delavbolag</strong>: Affärsområde (t.ex. "Fastighet", "Service")</li>
              </ul>
            </div>
            
            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
              <h3 className="flex items-center gap-2 text-white font-medium mb-2">
                <Code className="h-4 w-4 text-orange-300" />
                Format för inmatning
              </h3>
              <ul className="text-sm space-y-2">
                <li>• <strong>Dokument</strong>: "Dokumenttitel (https://url-till-dokument.com)"; 
                  Använd semikolon (;) för att skilja flera dokument åt</li>
                <li>• <strong>Deltagare och ansvar</strong>: "Roll: Ansvarar för x"; 
                  Använd semikolon (;) för att skilja olika roller åt</li>
                <li>• <strong>Detaljeradbeskrivning</strong>: Stöder flera stycken med radbrytningar</li>
                <li>• <strong>Listordning</strong>: Numeriskt värde för att ordna processen</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
            <h3 className="flex items-center gap-2 text-white font-medium mb-2">
              <FileText className="h-4 w-4 text-orange-300" />
              Tips och hjälp
            </h3>
            <ul className="text-sm space-y-2">
              <li>• Använd sökrutan för att hitta specifika processer, moment eller dokument</li>
              <li>• Klicka på dokumentlänkar för att förhandsgranska eller öppna dem</li>
              <li>• Processerna är hierarkiskt ordnade för enkel navigering</li>
              <li>• Indikatorer visar om ett moment har dokument eller deltagare</li>
              <li>• Du kan filtrera innehållet baserat på roller med filterknappen</li>
              <li>• För deltagare och ansvar kan du jämföra olika alternativ sida vid sida</li>
              <li>• Använd dokumentlistan för att se alla dokument i en samlad vy</li>
              <li>• Starta guiden genom att klicka på frågetecknet i övre menyn</li>
              <li>• Testa dina kunskaper med processquizen för att lära dig processerna</li>
            </ul>
          </div>
          
          <div className="text-sm text-gray-400">
            <p>För teknisk support eller frågor, kontakta IT-avdelningen.</p>
            <p className="mt-2">Version: 1.3.0</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InfoDialog;